import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Paper,
  OutlinedInput,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

const PracticeSelector = ({ centered = true }) => {
  const { user, authData } = useContext(AuthContext);
  const { selectedPractice, setSelectedPractice } = usePractice();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Debug logging
  console.log('PracticeSelector - User:', user);
  console.log('PracticeSelector - AuthData:', authData);
  console.log('PracticeSelector - Selected Practice:', selectedPractice);
  console.log('PracticeSelector - Practices:', practices);

  useEffect(() => {
    if (!user || user.role !== 'Personnel') {
      console.log('PracticeSelector - Not a provider or no user');
      setLoading(false);
      return;
    }

    if (authData?.organizations) {
      // use organizations from whoami
      setPractices(authData.organizations);
      // Set the first organization as selected by default if none is selected
      if (!selectedPractice && authData.organizations.length > 0) {
        console.log('PracticeSelector - Setting default organization:', authData.organizations[0]);
        setSelectedPractice(authData.organizations[0]);
      }
    }
    setLoading(false);
  }, [user, authData, selectedPractice, setSelectedPractice]);

  const handlePracticeChange = (event) => {
    const practiceId = event.target.value;
    const practice = practices.find(p => p.id === practiceId);
    if (practice) {
      console.log('PracticeSelector - Changing practice to:', practice);
      setSelectedPractice(practice);
    }
  };

  if (!user || user.role !== 'Personnel') {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (practices.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No practices available
        </Typography>
      </Box>
    );
  }

  // Helper: format a single-line address from addresses list
  const formatAddress = (addresses = []) => {
    if (!Array.isArray(addresses) || addresses.length === 0) return '';
    const preferred = ['billing', 'corporate', 'shipping'];
    const addr = preferred
      .map(p => addresses.find(a => a?.purpose === p))
      .find(Boolean) || addresses[0];
    if (!addr) return '';
    const line1 = [addr.street_1, addr.street_2].filter(Boolean).join(', ');
    const cityst = [addr.city, addr.state].filter(Boolean).join(', ');
    return [
      line1,
      [cityst, addr.zip_code].filter(Boolean).join(' ')
    ].filter(Boolean).join(', ');
  };

  return (
    <Box sx={{ 
      p: '2px 4px', 
      width: '100%',
      display: 'flex',
      justifyContent: centered ? 'center' : 'flex-start',
      alignItems: 'flex-end',
      minHeight: '32px',
      mb: 0.25,
      mt: 1, // nudge downward to avoid AppBar clipping the floating label
    }}>
      <FormControl
        fullWidth
        size="small"
        margin="dense"
        variant="outlined"
        sx={{
          maxWidth: '400px',
          '& .MuiOutlinedInput-root': { height: 28 },
          '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0, minHeight: 'unset' },
          '& .MuiInputLabel-root': { transform: 'translate(12px, 5px) scale(1)' },
          '& .MuiInputLabel-shrink': { transform: 'translate(12px, -6px) scale(0.75)' },
        }}
      >
        <InputLabel id="practice-select-label" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>Current Organization</InputLabel>
        <Select
          labelId="practice-select-label"
          value={selectedPractice?.id || ''}
          label="Current Organization"
          onChange={handlePracticeChange}
          size="small"
          renderValue={(selectedId) => {
            const p = practices.find(pr => pr.id === selectedId);
            if (!p) return '';
            // Compact single-line display to keep control short
            return p.name || 'Select organization';
          }}
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
            },
          }}
          input={
            <OutlinedInput
              label="Current Organization"
              sx={{
                height: 28,
                '& .MuiOutlinedInput-input': {
                  py: 0,
                },
                '& .MuiInputBase-input': {
                  py: 0,
                },
              }}
            />
          }
        >
          {practices.map((practice) => (
            <MenuItem key={practice.id} value={practice.id} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {practice.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatAddress(practice.addresses) || practice.address || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  NPI: {practice.npi || 'N/A'}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default PracticeSelector; 
